import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import { fetchNews } from "DBServices/firestoreDBService";

// ── Animations ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// ── Wrapper ────────────────────────────────────────────────────────────────────
const Section = styled.section`
  position: relative;
  padding: 5rem 1rem;
  background: linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%);
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const SectionBadge = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 4px 14px;
  border-radius: 9999px;
  margin-bottom: 12px;
  background: linear-gradient(135deg, #d1fae5, #6ee7b7);
  color: #065f46;
`;

const SectionHeading = styled.h2`
  font-size: 2.25rem;
  font-weight: 800;
  color: #111827;
  margin: 0 0 10px;
  line-height: 1.2;
`;

const SectionSubheading = styled.p`
  color: #6b7280;
  font-size: 1rem;
  max-width: 520px;
  margin-bottom: 3rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

// ── Card ───────────────────────────────────────────────────────────────────────
const Card = styled.a`
  display: block;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
  transition: transform 0.3s, box-shadow 0.3s;
  animation: ${fadeUp} 0.5s ease both;
  animation-delay: ${({ index }) => index * 0.08}s;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 32px rgba(16, 185, 129, 0.18);
  }
`;

const CardImage = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: #e5e7eb;
`;

const CardImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
  ${Card}:hover & { transform: scale(1.06); }
`;

const PlaceholderImg = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
`;

const CategoryPill = styled.span`
  position: absolute;
  top: 12px;
  left: 12px;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 9999px;
  z-index: 10;
  background: linear-gradient(135deg, #059669, #10b981);
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CardBody = styled.div`
  padding: 20px;
`;

const CardDate = styled.p`
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 500;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CardTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardExcerpt = styled.p`
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  padding: 0 20px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Source = styled.span`
  font-size: 0.72rem;
  color: #9ca3af;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 120px;
`;

const ReadMore = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #059669;
`;

// ── Featured (first item) ──────────────────────────────────────────────────────
const FeaturedCard = styled.a`
  display: block;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  text-decoration: none;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  animation: ${fadeUp} 0.4s ease both;
  margin-bottom: 2rem;
  transition: transform 0.3s, box-shadow 0.3s;

  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 40px rgba(16, 185, 129, 0.2);
  }
`;

const FeaturedImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  min-height: 280px;
  background: #e5e7eb;
`;

const FeaturedImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s;
  ${FeaturedCard}:hover & { transform: scale(1.04); }
`;

const FeaturedBody = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const FeaturedBadge = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 12px;
  display: inline-block;
  color: #059669;
`;

const FeaturedTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 16px;
  line-height: 1.3;
`;

const FeaturedExcerpt = styled.p`
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

const FeaturedReadMore = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  padding: 8px 20px;
  border-radius: 9999px;
  color: white;
  background: linear-gradient(135deg, #059669, #10b981);
`;

// ── Skeleton ────────────────────────────────────────────────────────────────
const SkeletonCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.07);
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  border-radius: 6px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 5rem 0;
  color: #9ca3af;
`;

// ── Helpers ────────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
};

const getDomain = (url) => {
  try { return new URL(url).hostname.replace("www.", ""); } catch { return url; }
};

// ── Component ──────────────────────────────────────────────────────────────────
const LatestNews = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews()
      .then((data) => {
        // Show only published news, newest first, max 7 items
        const sorted = (data || [])
          .filter((n) => n.published !== false)
          .sort((a, b) => new Date(b.publishedAt || b.createdAt || 0) - new Date(a.publishedAt || a.createdAt || 0));
        setNews(sorted.slice(0, 7));
      })
      .catch(() => setNews([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = news[0];
  const rest = news.slice(1, 7);

  return (
    <Section>
      <Inner>
        {/* Heading */}
        <div style={{ marginBottom: 16 }}>
          <SectionBadge>📰 Latest News</SectionBadge>
          <SectionHeading>Stay in the Know</SectionHeading>
          <SectionSubheading>
            Fresh updates from food markets, prices, and agriculture news across Nigeria.
          </SectionSubheading>
        </div>

        {/* Skeleton */}
        {loading && (
          <>
            <SkeletonCard style={{ height: 280, marginBottom: 32 }}>
              <SkeletonBlock style={{ height: "100%", borderRadius: 16 }} />
            </SkeletonCard>
            <Grid>
              {[...Array(6)].map((_, i) => (
                <SkeletonCard key={i}>
                  <SkeletonBlock style={{ height: 180 }} />
                  <div style={{ padding: 20 }}>
                    <SkeletonBlock style={{ height: 12, width: "40%", marginBottom: 10 }} />
                    <SkeletonBlock style={{ height: 16, marginBottom: 8 }} />
                    <SkeletonBlock style={{ height: 14, width: "80%", marginBottom: 6 }} />
                    <SkeletonBlock style={{ height: 14, width: "60%" }} />
                  </div>
                </SkeletonCard>
              ))}
            </Grid>
          </>
        )}

        {/* Empty */}
        {!loading && news.length === 0 && (
          <EmptyState>
            <span style={{ fontSize: "3rem" }}>📭</span>
            <p style={{ marginTop: 12, fontWeight: 600 }}>No news yet. Check back soon!</p>
          </EmptyState>
        )}

        {/* Featured Article */}
        {!loading && featured && (
          <FeaturedCard href={featured.url} target="_blank" rel="noopener noreferrer">
            <FeaturedImageWrap>
              {featured.imageSrc
                ? <FeaturedImg src={featured.imageSrc} alt={featured.title} onError={e => { e.target.style.display = "none"; }} />
                : (
                  <PlaceholderImg>
                    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="#6ee7b7" strokeWidth="2">
                      <rect x="8" y="12" width="48" height="40" rx="4" />
                      <path d="M8 24h48" />
                      <path d="M20 12v12M44 12v12" />
                    </svg>
                  </PlaceholderImg>
                )
              }
              {featured.category && <CategoryPill>{featured.category}</CategoryPill>}
            </FeaturedImageWrap>
            <FeaturedBody>
              <FeaturedBadge>Featured Story</FeaturedBadge>
              <FeaturedTitle>{featured.title}</FeaturedTitle>
              <FeaturedExcerpt>{featured.excerpt || featured.description}</FeaturedExcerpt>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
                <CardDate>
                  <span>🕐</span>
                  {formatDate(featured.publishedAt || featured.createdAt)}
                  {featured.source && <> · <span style={{ color: "#059669" }}>{featured.source}</span></>}
                </CardDate>
                <FeaturedReadMore>
                  Read Full Story
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </FeaturedReadMore>
              </div>
            </FeaturedBody>
          </FeaturedCard>
        )}

        {/* Grid of remaining articles */}
        {!loading && rest.length > 0 && (
          <Grid>
            {rest.map((item, i) => (
              <Card key={item.firestoreId || i} href={item.url} target="_blank" rel="noopener noreferrer" index={i}>
                <CardImage>
                  {item.imageSrc
                    ? <CardImg src={item.imageSrc} alt={item.title} onError={e => { e.target.style.display = "none"; }} />
                    : (
                      <PlaceholderImg>
                        <svg viewBox="0 0 64 64" width="48" height="48" fill="none" stroke="#6ee7b7" strokeWidth="2">
                          <rect x="8" y="12" width="48" height="40" rx="4" />
                          <path d="M8 24h48M20 12v12M44 12v12" />
                        </svg>
                      </PlaceholderImg>
                    )
                  }
                  {item.category && <CategoryPill>{item.category}</CategoryPill>}
                </CardImage>
                <CardBody>
                  <CardDate>
                    <span>🕐</span>
                    {formatDate(item.publishedAt || item.createdAt)}
                  </CardDate>
                  <CardTitle>{item.title}</CardTitle>
                  <CardExcerpt>{item.excerpt || item.description}</CardExcerpt>
                </CardBody>
                <CardFooter>
                  <Source>{getDomain(item.url)}</Source>
                  <ReadMore>
                    Read more
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </ReadMore>
                </CardFooter>
              </Card>
            ))}
          </Grid>
        )}
      </Inner>
    </Section>
  );
};

export default LatestNews;
